/**
 * Shared pieces of the desk operations screens (OD-71): panels, fact lists, pills, amount cells and the
 * action form every command goes through (reason when the server needs one; the device key signs the
 * step-up silently). Amounts are server decimal strings; nothing here computes money.
 */
import { cx, formatAmount, useToast } from '@hopex/ui';
import { useState, type ReactNode } from 'react';
import { ApiFailure, errorMessage } from '../lib/api.ts';

export type Tone = 'positive' | 'negative' | 'pending' | 'neutral' | 'accent';

export const toman = (v: string | null) => (
  <bdi dir="ltr" className="hx-num">
    {formatAmount(v, 'TOMAN')}
  </bdi>
);
export const coin = (v: string | null, asset: string) => (
  <bdi dir="ltr" className="hx-num">
    {formatAmount(v, asset)}
  </bdi>
);
export const amountOf = (v: string | null, asset: string) =>
  asset === 'IRT' || asset === 'TOMAN' ? toman(v) : coin(v, asset);

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[11.5px] font-bold',
        tone === 'positive' && 'bg-positive-tint text-positive',
        tone === 'negative' && 'bg-negative-tint text-negative',
        tone === 'pending' &&
          'bg-[color-mix(in_srgb,var(--hx-pending)_16%,transparent)] text-[var(--hx-pending)]',
        tone === 'accent' && 'bg-accent-tint text-accent-text',
        tone === 'neutral' && 'bg-surface-2 text-fg-muted',
      )}
    >
      {children}
    </span>
  );
}

export function Panel({
  title,
  icon,
  aside,
  children,
  className,
  ...rest
}: {
  title?: ReactNode;
  icon?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
} & Record<`data-${string}`, string | boolean | undefined>) {
  return (
    <section
      className={cx('rounded-[22px] border border-line bg-surface-1 p-4 shadow-sm', className)}
      {...rest}
    >
      {(title || aside) && (
        <header className="mb-3 flex items-center gap-2">
          {icon && (
            <span className="grid size-8 shrink-0 place-items-center rounded-[11px] bg-accent-tint text-accent-text">
              {icon}
            </span>
          )}
          <h3 className="m-0 min-w-0 flex-1 truncate text-[14.5px] font-extrabold">{title}</h3>
          {aside}
        </header>
      )}
      {children}
    </section>
  );
}

/** Label / value rows that wrap nicely on a phone and sit in two columns on a wide screen. */
export function Facts({ rows, cols = 1 }: { rows: [string, ReactNode][]; cols?: 1 | 2 }) {
  return (
    <dl
      className={cx(
        'm-0 grid gap-x-6 gap-y-0 text-[13px]',
        cols === 2 ? 'sm:grid-cols-2' : 'grid-cols-1',
      )}
    >
      {rows.map(([k, v]) => (
        <div
          key={k}
          className="flex min-w-0 items-baseline justify-between gap-3 border-b border-line/70 py-2.5 last:border-b-0"
        >
          <dt className="shrink-0 text-fg-muted">{k}</dt>
          <dd className="m-0 min-w-0 break-words text-end font-semibold text-fg">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 rounded-[16px] border border-dashed border-line-strong px-3 py-6 text-center text-[13px] text-fg-muted">
      {children}
    </p>
  );
}

export function Loading() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="در حال بارگذاری">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-20 animate-pulse rounded-[22px] bg-surface-2" />
      ))}
    </div>
  );
}

// ───────── commands ─────────
export type Field = {
  key: string;
  label: string;
  value?: string;
  ltr?: boolean;
  required?: boolean;
};
export interface Act {
  id: string;
  label: string;
  hint?: string;
  tone: 'positive' | 'negative' | 'neutral';
  fields: Field[];
  checkbox?: { key: string; label: string };
  /** Fixed choices rendered as chips (e.g. the asset of a balance adjustment). */
  choices?: { key: string; label: string; options: { value: string; label: string }[] };
  run: (v: Record<string, string>, checked: boolean) => Promise<unknown>;
}

export function ActButton({ a, onPick }: { a: Act; onPick: (a: Act) => void }) {
  return (
    <button
      type="button"
      onClick={() => onPick(a)}
      data-ops-act={a.id}
      className={cx(
        'hx-press flex min-h-12 w-full cursor-pointer flex-col items-start justify-center rounded-[16px] border px-3.5 py-2 text-start text-[13.5px] font-extrabold',
        a.tone === 'positive' &&
          'border-[color-mix(in_srgb,var(--hx-positive)_45%,transparent)] bg-[color-mix(in_srgb,var(--hx-positive)_8%,transparent)] text-positive',
        a.tone === 'negative' &&
          'border-[color-mix(in_srgb,var(--hx-negative)_45%,transparent)] bg-[color-mix(in_srgb,var(--hx-negative)_6%,transparent)] text-negative',
        a.tone === 'neutral' && 'border-line-strong bg-surface-2 text-fg',
      )}
    >
      {a.label}
      {a.hint && <span className="text-[11px] font-medium text-fg-muted">{a.hint}</span>}
    </button>
  );
}

export function ActForm({
  act,
  onCancel,
  onDone,
}: {
  act: Act;
  onCancel: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [v, setV] = useState<Record<string, string>>(() => ({
    ...Object.fromEntries(act.fields.map((f) => [f.key, f.value ?? ''])),
    ...(act.choices ? { [act.choices.key]: act.choices.options[0]?.value ?? '' } : {}),
  }));
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ok =
    act.fields.every(
      (f) => !f.required || (v[f.key]?.trim().length ?? 0) >= (f.key === 'reason' ? 3 : 1),
    ) &&
    (!act.choices || !!v[act.choices.key]) &&
    (!act.checkbox || checked);
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await act.run(Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x.trim()])), checked);
      toast.show({ tone: 'success', title: 'انجام شد' });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !busy) void submit();
      }}
      data-ops-form={act.id}
    >
      {act.choices && (
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
          <legend className="mb-1.5 text-[12.5px] font-semibold text-fg-muted">
            {act.choices.label}
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {act.choices.options.map((o) => {
              const on = v[act.choices?.key ?? ''] === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setV((x) => ({ ...x, [act.choices?.key ?? '']: o.value }))}
                  className={cx(
                    'hx-press min-h-9 cursor-pointer rounded-full border px-3 text-[12.5px] font-bold',
                    on
                      ? 'border-accent bg-accent text-on-accent'
                      : 'border-line-strong bg-surface-2 text-fg',
                  )}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
      {act.fields.map((f) => (
        <label key={f.key} className="flex flex-col gap-1">
          <span className="text-[12.5px] font-semibold text-fg-muted">{f.label}</span>
          <input
            value={v[f.key] ?? ''}
            onChange={(e) => setV((o) => ({ ...o, [f.key]: e.target.value }))}
            dir={f.ltr ? 'ltr' : 'rtl'}
            inputMode={f.key === 'amount' ? 'decimal' : undefined}
            autoComplete="off"
            className="min-h-11 rounded-[14px] border border-line-strong bg-surface-2 px-3 text-[16px] text-fg outline-hidden focus-visible:border-accent"
          />
        </label>
      ))}
      {act.checkbox && (
        <label className="flex items-start gap-2 rounded-[14px] bg-surface-2 p-3 text-[12.5px] text-fg">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            className="mt-0.5 size-4 accent-[var(--hx-accent)]"
          />
          {act.checkbox.label}
        </label>
      )}
      {err && (
        <p
          role="alert"
          className="m-0 rounded-[12px] bg-negative-tint px-3 py-2 text-[12.5px] text-negative"
        >
          {err}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="min-h-12 flex-1 cursor-pointer rounded-[14px] border border-line-strong text-[14px] font-bold"
        >
          انصراف
        </button>
        <button
          type="submit"
          disabled={!ok || busy}
          className={cx(
            'hx-press min-h-12 flex-[2] cursor-pointer rounded-[14px] text-[14px] font-extrabold disabled:opacity-50',
            act.tone === 'negative'
              ? 'bg-negative text-canvas'
              : act.tone === 'positive'
                ? 'bg-positive text-canvas'
                : 'bg-accent text-on-accent shadow-cta',
          )}
        >
          {busy ? 'در حال انجام…' : act.label}
        </button>
      </div>
    </form>
  );
}
